import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Cpu, DollarSign, Layers } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { UserDetailDialog } from "../components/UserDetailDialog";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [workspaces, setWorkspaces] = useState(null);
  const [payments, setPayments] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const refresh = useCallback(() => {
    api.get("/superadmin/users").then(({ data }) => setUsers(data));
    api.get("/superadmin/workspaces").then(({ data }) => setWorkspaces(data));
    api.get("/superadmin/payments").then(({ data }) => setPayments(data));
    api.get("/superadmin/ai-usage").then(({ data }) => setAiUsage(data));
  }, []);

  useEffect(() => {
    if (!user?.is_superadmin) return;
    refresh();
  }, [user, refresh]);

  if (!user?.is_superadmin) {
    return <div className="text-sm text-muted-foreground" data-testid="superadmin-access-denied">Access denied. Superadmin only.</div>;
  }

  return (
    <div>
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-primary" />
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Super Admin Console</h1>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Platform-wide view of every user, workspace and payment.</p>

      <Tabs defaultValue="users" className="mt-6">
        <TabsList data-testid="superadmin-tabs">
          <TabsTrigger value="users" data-testid="superadmin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="workspaces" data-testid="superadmin-tab-workspaces">Workspaces</TabsTrigger>
          <TabsTrigger value="payments" data-testid="superadmin-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="ai-usage" data-testid="superadmin-tab-ai-usage">AI Usage</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm" data-testid="superadmin-users-table">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Workspaces</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {(users || []).map((u) => (
                  <tr key={u.id} className="border-t border-border/60" data-testid={`superadmin-user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium">{u.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3 font-mono">{u.workspace_count}</td>
                    <td className="px-4 py-3">
                      {u.is_superadmin ? (
                        <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">Superadmin</span>
                      ) : (
                        <span className="text-muted-foreground">User</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => setSelectedUserId(u.id)} data-testid={`superadmin-view-user-btn-${u.id}`}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <UserDetailDialog
          userId={selectedUserId}
          open={!!selectedUserId}
          onOpenChange={(open) => !open && setSelectedUserId(null)}
          onChanged={refresh}
        />

        <TabsContent value="workspaces">
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm" data-testid="superadmin-workspaces-table">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Workspace</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Credits Left</th>
                  <th className="px-4 py-3">Job Quota</th>
                  <th className="px-4 py-3">Members</th>
                  <th className="px-4 py-3">Jobs</th>
                  <th className="px-4 py-3">Candidates</th>
                </tr>
              </thead>
              <tbody>
                {(workspaces || []).map((w) => (
                  <tr key={w.id} className="border-t border-border/60">
                    <td className="px-4 py-3 font-medium">{w.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{w.owner_email || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${w.plan !== "none" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                        {w.plan}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono">{w.credits_balance}</td>
                    <td className="px-4 py-3 font-mono">{w.job_count}/{w.max_jobs}</td>
                    <td className="px-4 py-3 font-mono">{w.member_count}</td>
                    <td className="px-4 py-3 font-mono">{w.job_count}</td>
                    <td className="px-4 py-3 font-mono">{w.candidate_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="payments">
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm" data-testid="superadmin-payments-table">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Order ID</th>
                  <th className="px-4 py-3">Workspace</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Credits Added</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {(payments || []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No payments yet</td>
                  </tr>
                ) : (
                  payments.map((p) => (
                    <tr key={p.id} className="border-t border-border/60">
                      <td className="px-4 py-3 font-mono text-xs">{p.order_id}{p.test_mode && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">TEST</span>}</td>
                      <td className="px-4 py-3">{p.workspace_name}</td>
                      <td className="px-4 py-3 font-mono">₹{Number(p.amount_inr).toFixed(2)}</td>
                      <td className="px-4 py-3 font-mono">{p.credits_added}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${p.status === "paid" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" : "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300"}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="ai-usage">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="ai-usage-summary-cards">
            <UsageCard label="Total Evaluations" value={aiUsage?.totals.evaluations ?? "—"} icon={Layers} />
            <UsageCard label="Total Tokens Consumed" value={aiUsage ? aiUsage.totals.total_tokens.toLocaleString() : "—"} icon={Cpu} />
            <UsageCard
              label="Total Estimated Cost"
              value={aiUsage ? `$${aiUsage.totals.total_cost_usd.toFixed(4)}` : "—"}
              icon={DollarSign}
              accent
            />
          </div>

          <h3 className="mt-8 font-heading text-sm font-semibold text-muted-foreground">Cost by Job (batch total for all resumes uploaded to that job)</h3>
          <div className="mt-3 overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm" data-testid="superadmin-ai-usage-by-job-table">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Job</th>
                  <th className="px-4 py-3">Workspace</th>
                  <th className="px-4 py-3">Resumes Evaluated</th>
                  <th className="px-4 py-3">Total Tokens</th>
                  <th className="px-4 py-3">Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {(aiUsage?.by_job || []).length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No AI evaluations yet</td></tr>
                ) : (
                  aiUsage.by_job.map((j) => (
                    <tr key={j.job_id} className="border-t border-border/60">
                      <td className="px-4 py-3 font-medium">{j.job_title}</td>
                      <td className="px-4 py-3 text-muted-foreground">{j.workspace_name}</td>
                      <td className="px-4 py-3 font-mono">{j.evaluations}</td>
                      <td className="px-4 py-3 font-mono">{j.total_tokens.toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono font-semibold text-primary">${j.total_cost_usd.toFixed(4)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <h3 className="mt-8 font-heading text-sm font-semibold text-muted-foreground">Per-Transaction Breakdown (each resume evaluation = one OpenAI call)</h3>
          <div className="mt-3 overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm" data-testid="superadmin-ai-usage-transactions-table">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Candidate</th>
                  <th className="px-4 py-3">Job / Workspace</th>
                  <th className="px-4 py-3">Prompt Tokens</th>
                  <th className="px-4 py-3">Completion Tokens</th>
                  <th className="px-4 py-3">Total Tokens</th>
                  <th className="px-4 py-3">Cost</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {(aiUsage?.transactions || []).length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">No AI evaluations yet</td></tr>
                ) : (
                  aiUsage.transactions.map((t) => (
                    <tr key={t.id} className="border-t border-border/60" data-testid={`ai-usage-transaction-${t.id}`}>
                      <td className="px-4 py-3 font-medium">{t.candidate_name}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{t.job_title} · {t.workspace_name}</td>
                      <td className="px-4 py-3 font-mono">{t.prompt_tokens.toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono">{t.completion_tokens.toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono">{t.total_tokens.toLocaleString()}</td>
                      <td className="px-4 py-3 font-mono font-semibold">${t.estimated_cost_usd.toFixed(5)}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UsageCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-xl border border-border/80 bg-card p-5 shadow-sm">
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent ? "bg-emerald-50 dark:bg-emerald-950/40" : "bg-primary/10"}`}>
        <Icon className={`h-4 w-4 ${accent ? "text-emerald-600 dark:text-emerald-400" : "text-primary"}`} />
      </div>
      <div className={`mt-3 font-mono text-2xl font-bold ${accent ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"}`}>{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}
